/**
 * Email utilities for building email compose links across multiple providers
 * Supports: mailto (default), Gmail, Outlook (Office 365), Outlook.com, Yahoo
 */

import i18n from '@/app/i18n/i18n';

// =============================================================================
// Types
// =============================================================================

/**
 * Supported email providers
 * - default: OS/browser default mail client via mailto:
 * - gmail: Gmail web compose
 * - outlookWeb: Outlook Office 365 (outlook.office.com)
 * - outlookLive: Outlook.com / Hotmail (outlook.live.com)
 * - yahoo: Yahoo Mail
 */
export type EmailProvider = 'default' | 'gmail' | 'outlookWeb' | 'outlookLive' | 'yahoo';

/**
 * All available email providers with their display labels
 */
export const EMAIL_PROVIDERS: { value: EmailProvider; label: string }[] = [
  { value: 'default', label: 'Standaard mailprogramma' },
  { value: 'gmail', label: 'Gmail' },
  { value: 'outlookWeb', label: 'Outlook (Office 365)' },
  { value: 'outlookLive', label: 'Outlook.com / Hotmail' },
  { value: 'yahoo', label: 'Yahoo Mail' },
];

export interface CustomerEmailData {
  email?: string | null;
  /** Phone number for WhatsApp messaging */
  phone?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
}

export type EmailContext = 'customer' | 'workOrder' | 'invoice' | 'appointment';

export interface BuildSubjectAndBodyOptions {
  customer: CustomerEmailData;
  context: EmailContext;
  garageName?: string;
  // Context-specific data (for future use)
  workOrderId?: string;
  invoiceNumber?: string;
  licensePlate?: string;
  appointmentDate?: string;
}

// =============================================================================
// Helper: Compute display name from customer data
// =============================================================================

/**
 * Computes the best display name for a customer
 * Priority: displayName > companyName > firstName + lastName > "klant"
 */
export function getCustomerDisplayName(customer: CustomerEmailData): string {
  if (customer.displayName) {
    return customer.displayName;
  }
  if (customer.companyName) {
    return customer.companyName;
  }
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
  if (fullName) {
    return fullName;
  }
  return 'klant';
}

// =============================================================================
// Helper: Build email compose URL for any provider
// =============================================================================

interface BuildEmailLinkOptions {
  provider: EmailProvider;
  to: string;
  subject?: string;
  body?: string;
}

/**
 * Builds an email compose URL for the specified provider
 * 
 * @param provider - Email provider to use
 * @param to - Recipient email address
 * @param subject - Email subject (optional)
 * @param body - Email body text (optional)
 * @returns URL string that opens the compose window for the provider
 */
export function buildEmailLink({ provider, to, subject = '', body = '' }: BuildEmailLinkOptions): string {
  const encodedTo = encodeURIComponent(to);
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  switch (provider) {
    case 'gmail':
      // Gmail web compose URL
      return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodedTo}&su=${encodedSubject}&body=${encodedBody}`;

    case 'outlookWeb':
      // Outlook Office 365 (work/school accounts)
      return `https://outlook.office.com/mail/deeplink/compose?to=${encodedTo}&subject=${encodedSubject}&body=${encodedBody}`;

    case 'outlookLive':
      // Outlook.com / Hotmail (personal accounts)
      return `https://outlook.live.com/owa/?path=/mail/action/compose&to=${encodedTo}&subject=${encodedSubject}&body=${encodedBody}`;

    case 'yahoo':
      // Yahoo Mail compose
      return `https://compose.mail.yahoo.com/?to=${encodedTo}&subject=${encodedSubject}&body=${encodedBody}`;

    case 'default':
    default:
      // Standard mailto: link for OS/browser default mail client
      return `mailto:${to}?subject=${encodedSubject}&body=${encodedBody}`;
  }
}

// =============================================================================
// Legacy helper (kept for backward compatibility)
// =============================================================================

interface BuildMailtoOptions {
  email: string;
  subject: string;
  body: string;
}

/**
 * @deprecated Use buildEmailLink with provider='default' instead
 * Builds a properly URL-encoded mailto: link
 */
export function buildCustomerMailto({ email, subject, body }: BuildMailtoOptions): string {
  return buildEmailLink({ provider: 'default', to: email, subject, body });
}

// =============================================================================
// Helper: Build email closing block
// =============================================================================

export interface EmailClosingParams {
  /** Closing text from settings (e.g., "Met vriendelijke groet,") */
  closingText?: string;
  /** User's full name from profile */
  userFullName?: string;
  /** Company/BV name (for invoice context) */
  companyName?: string;
}

/**
 * Get default closing text from i18n
 */
export const getDefaultClosingText = () => i18n.t('email.defaultClosing', { ns: 'common' });

/**
 * Default closing text if none is configured
 * @deprecated Use getDefaultClosingText() for i18n support
 */
export const DEFAULT_CLOSING_TEXT = 'Met vriendelijke groet,';

/**
 * Builds the email closing block with closing text, user name, and optional company name.
 * 
 * Example output:
 * ```
 * Met vriendelijke groet,
 * 
 * Belle van Oostenbruggen
 * ModCar B.V.
 * ```
 * 
 * @param params - Closing block parameters
 * @returns Formatted closing block string
 */
export function buildEmailClosingBlock(params: EmailClosingParams): string {
  const { closingText, userFullName, companyName } = params;
  
  const lines: string[] = [];
  
  // Closing text (use default if not set)
  lines.push(closingText || getDefaultClosingText());
  
  // Blank line after closing text
  lines.push('');
  
  // User's full name
  if (userFullName) {
    lines.push(userFullName);
  }
  
  // Company name (typically for invoice context)
  if (companyName) {
    lines.push(companyName);
  }
  
  return lines.join('\n');
}

// =============================================================================
// Helper: Build subject and body based on context
// =============================================================================

/**
 * Generates email subject and body based on the usage context
 * 
 * @param options - Context and customer data
 * @returns Object with subject and body strings
 */
export function buildSubjectAndBodyFromContext(options: BuildSubjectAndBodyOptions): { subject: string; body: string } {
  const { customer, context, garageName = 'Ons bedrijf' } = options;
  const customerName = getCustomerDisplayName(customer);
  
  switch (context) {
    case 'customer':
      return {
        subject: 'Vraag over uw auto',
        body: `Beste ${customerName},

Ik heb een vraag over uw auto.

Met vriendelijke groet,
${garageName}`,
      };
    
    // TODO: Implement workOrder context
    // case 'workOrder':
    //   const { workOrderId, licensePlate } = options;
    //   return {
    //     subject: `Werkorder ${workOrderId} - ${licensePlate}`,
    //     body: `Beste ${customerName},
    //
    // Betreft: Werkorder ${workOrderId} voor voertuig ${licensePlate}
    //
    // Met vriendelijke groet,
    // ${garageName}`,
    //   };
    
    // TODO: Implement invoice context
    // case 'invoice':
    //   const { invoiceNumber } = options;
    //   return {
    //     subject: `Factuur ${invoiceNumber}`,
    //     body: `Beste ${customerName},
    //
    // Betreft: Factuur ${invoiceNumber}
    //
    // Met vriendelijke groet,
    // ${garageName}`,
    //   };
    
    // TODO: Implement appointment context
    // case 'appointment':
    //   const { appointmentDate, licensePlate } = options;
    //   return {
    //     subject: `Afspraak ${appointmentDate}`,
    //     body: `Beste ${customerName},
    //
    // Betreft: Uw afspraak op ${appointmentDate}
    //
    // Met vriendelijke groet,
    // ${garageName}`,
    //   };
    
    default:
      // Fallback for unimplemented contexts
      return {
        subject: 'Vraag over uw auto',
        body: `Beste ${customerName},



Met vriendelijke groet,
${garageName}`,
      };
  }
}
